import React, { useId, useRef, useState } from 'react';
import ImageCropModal from './ImageCropModal';
import { getCroppedImageBlob } from '../utils/cropImage';
import { resolveMediaUrl } from '../utils/media';
import { HEIC_UNSUPPORTED_MESSAGE, isHeicFile } from '../utils/uploadLimits';
import './ThumbnailPicker.css';

const EXTENSIONS_BY_TYPE = {
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
};

// The canvas falls back to PNG for types it cannot encode (e.g. GIF), so
// name and label the cropped file by what was actually produced.
const croppedFileFor = (blob, originalFile) => {
    const type = blob.type || originalFile.type;
    const extension = EXTENSIONS_BY_TYPE[type];
    const name = extension && type !== originalFile.type
        ? originalFile.name.replace(/(\.[^./\\]+)?$/, extension)
        : originalFile.name;
    return new File([blob], name, { type });
};

const ThumbnailPicker = ({
    value,
    onChange,
    onUploadImage,
    label = 'Thumbnail (optional)',
    hint,
    disabled = false,
    aspect = 1,
    onError,
    suggestions = [],
}) => {
    const fileInputRef = useRef(null);
    const suggestionsLabelId = useId();
    const [isCropping, setIsCropping] = useState(false);
    const [croppingSource, setCroppingSource] = useState(null);
    const [originalFile, setOriginalFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    // Shown right here (and inside the cropper while it is open): the form's
    // own alert sits at its top, usually scrolled out of view by now.
    const [error, setError] = useState('');
    // Suggestion images that failed to load (e.g. a removed video's still).
    const [brokenSuggestions, setBrokenSuggestions] = useState(() => new Set());

    const resetCroppingState = () => {
        if (croppingSource) {
            URL.revokeObjectURL(croppingSource);
        }
        setCroppingSource(null);
        setOriginalFile(null);
        setIsCropping(false);
    };

    const reportError = (failure) => {
        setError(failure?.message || 'Failed to upload image');
        onError?.(failure);
    };

    // Any new choice replaces the last failure.
    const choose = (nextValue) => {
        setError('');
        onChange?.(nextValue);
    };

    const handleUploadClick = () => {
        if (disabled || uploading) {
            return;
        }
        setError('');
        fileInputRef.current?.click();
    };

    const handleFileSelection = async (event) => {
        const [file] = event.target.files || [];
        event.target.value = '';

        if (!file) {
            return;
        }

        // The cropper can't display HEIC in most browsers; say why up front.
        if (isHeicFile(file)) {
            reportError(new Error(HEIC_UNSUPPORTED_MESSAGE));
            return;
        }

        const previewUrl = URL.createObjectURL(file);
        setCroppingSource(previewUrl);
        setOriginalFile(file);
        setIsCropping(true);
    };

    const handleCropCancel = () => {
        resetCroppingState();
    };

    const handleCropConfirm = async (croppedAreaPixels) => {
        if (!croppingSource || !originalFile) {
            return;
        }

        setError('');
        setUploading(true);
        try {
            const blob = await getCroppedImageBlob(croppingSource, croppedAreaPixels, originalFile.type);
            const croppedFile = croppedFileFor(blob, originalFile);
            const uploadedUrl = await onUploadImage?.(croppedFile);
            if (uploadedUrl) {
                choose(uploadedUrl);
                resetCroppingState();
            }
        } catch (uploadError) {
            reportError(uploadError);
        } finally {
            setUploading(false);
        }
    };

    const handleManualInput = (event) => {
        choose(event.target.value);
    };

    const handleRemove = () => {
        choose('');
    };

    const handleSuggestionError = (src) => {
        setBrokenSuggestions((previous) => new Set(previous).add(src));
    };

    const visibleSuggestions = (Array.isArray(suggestions) ? suggestions : [])
        .filter((suggestion) => suggestion?.src && !brokenSuggestions.has(suggestion.src));
    const currentValue = typeof value === 'string' ? value.trim() : '';
    const currentSrc = resolveMediaUrl(currentValue);

    return (
        <div className="thumbnail-picker">
            <label className="thumbnail-picker__label">
                {label}
            </label>
            <div className="form-control-group">
                <input
                    type="text"
                    inputMode="url"
                    value={value}
                    onChange={handleManualInput}
                    placeholder="/api/images/<id> or https://example.com/preview.jpg"
                    disabled={disabled || uploading}
                />
                <div className="upload-buttons">
                    <button
                        type="button"
                        className="upload-button"
                        onClick={handleUploadClick}
                        disabled={disabled || uploading}
                    >
                        {uploading ? 'Uploading…' : 'Upload'}
                    </button>
                    {value && (
                        <button
                            type="button"
                            className="clear-button"
                            onClick={handleRemove}
                            disabled={disabled || uploading}
                        >
                            Remove
                        </button>
                    )}
                </div>
            </div>
            {error && (
                <p className="thumbnail-picker__error" role="alert">{error}</p>
            )}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileSelection}
                disabled={disabled || uploading}
            />
            {visibleSuggestions.length > 0 && (
                <div className="thumbnail-picker__suggestions">
                    <span className="thumbnail-picker__suggestions-label" id={suggestionsLabelId}>
                        Use an image from this post
                    </span>
                    <div
                        className="thumbnail-picker__suggestion-grid"
                        role="group"
                        aria-labelledby={suggestionsLabelId}
                    >
                        {visibleSuggestions.map((suggestion) => {
                            const pickValue = suggestion.value || suggestion.src;
                            const selected = Boolean(currentValue)
                                && (currentValue === pickValue || currentSrc === suggestion.src);
                            return (
                                <button
                                    key={suggestion.src}
                                    type="button"
                                    className={`thumbnail-picker__suggestion${selected ? ' is-selected' : ''}`}
                                    onClick={() => choose(pickValue)}
                                    disabled={disabled || uploading}
                                    aria-pressed={selected}
                                    title={`Use ${suggestion.label || 'this image'} as the thumbnail`}
                                >
                                    <img
                                        src={suggestion.src}
                                        alt=""
                                        loading="lazy"
                                        onError={() => handleSuggestionError(suggestion.src)}
                                    />
                                    <span className="thumbnail-picker__suggestion-label">
                                        {suggestion.label || 'Image'}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
            {value && (
                <div className="thumbnail-preview" style={{ aspectRatio: String(aspect) }}>
                    <img src={currentSrc} alt="Thumbnail preview" />
                </div>
            )}
            {uploading && (
                <div className="thumbnail-uploading">Uploading cropped image…</div>
            )}
            {hint && <small>{hint}</small>}

            <ImageCropModal
                open={isCropping}
                imageSrc={croppingSource}
                aspect={aspect}
                onCancel={handleCropCancel}
                onConfirm={handleCropConfirm}
                loading={uploading}
                error={error}
            />
        </div>
    );
};

export default ThumbnailPicker;
